import {
	PropsWithChildren,
	ReactElement,
	ReactNode,
	useMemo,
	useState
} from "react";
import { HubGridViewLayout } from "../HubGridViewLayout/HubGridViewLayout";
import { flattenChildren } from "client/Misc/reactUtils";
import { useChildrenAndMoreButton } from "../useChildrenAndMoreButton";
import { localize } from "client_legacy/ndaw/culture";
import { Button } from "client/Components";
import { generateTrackingId } from "client/Misc/utils";
import styles from "./LinkedDocumentsList.module.less";

export type LinkedDocumentsListMoreButtonText = {
	expanded: string;
	collapsed: string;
};

export type LinkedDocumentsListProps = {
	trackingId: string;
	moreButtonText?: LinkedDocumentsListMoreButtonText;
};

export const LinkedDocumentsList = (props: PropsWithChildren<LinkedDocumentsListProps>): ReactElement => {
	const {
		children,
		trackingId,
		moreButtonText
	} = props;

	const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

	const childrenArray = useMemo(
		(): readonly ReactNode[] => {
			return flattenChildren(children);
		},
		[children]
	);

	const {
		childrenToRender,
		moreButtonToRender,
		containerRef,
		itemRef
	} = useChildrenAndMoreButton({
		childrenArray,
		itemGap: 8,
		collapseMode: {
			kind: "limited-rows",
			isCollapsed,
			maxRowsCount: 2
		},
		renderMoreButton: (
			<MoreButton
				isExpanded={!isCollapsed}
				onClick={() => setIsCollapsed((x) => !x)}
				moreButtonText={moreButtonText}
			/>
		)
	});

	return (
		<>
			<div className={styles.root}>
				<HubGridViewLayout>
					<HubGridViewLayout.List trackingId={trackingId}>
						{childrenToRender}
					</HubGridViewLayout.List>
				</HubGridViewLayout>
				<div className={styles.measurement}>
					<HubGridViewLayout>
						<HubGridViewLayout.List
							ref={containerRef}
							trackingId={generateTrackingId(trackingId, "__measurement")}
						>
							<li ref={itemRef} />
						</HubGridViewLayout.List>
					</HubGridViewLayout>
				</div>
			</div>
			{moreButtonToRender}
		</>
	);
};
LinkedDocumentsList.displayName = "LinkedDocumentsList";

type MoreButtonProps = {
	isExpanded: boolean;
	moreButtonText?: LinkedDocumentsListMoreButtonText;
	onClick(): void;
};

const MoreButton = (props: MoreButtonProps): ReactElement => {
	const {
		isExpanded,
		moreButtonText,
		onClick
	} = props;

	const text: string = isExpanded
		? moreButtonText?.expanded ?? localize("_ShowLessLinkedDocuments")
		: moreButtonText?.collapsed ?? localize("_ShowAllLinkedDocuments");

	return (
		<div className={styles.moreButtonContainer}>
			<Button
				kind="transparent"
				icon={isExpanded
					? "chevron-up"
					: "chevron-down"
				}
				onClick={onClick}
				text={text}
				trackingId="more-linked-documents"
			/>
		</div>
	);
};
MoreButton.displayName = "MoreButton";